import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Use the properly typed interface from types/gelato.ts
import type { ArtworkOrder, OrderStatus } from '@/types/gelato';

export type CustomerArtworkOrder = Pick<ArtworkOrder,
  | 'id'
  | 'original_image_url'
  | 'generated_image_url'
  | 'selected_style'
  | 'format_size'
  | 'price_cents'
  | 'status'
  | 'customer_name'
  | 'payment_status'
  | 'finish_type'
  | 'frame_option'
  | 'support_material'
  | 'special_instructions'
  | 'created_at'
  | 'updated_at'
>;

export const useArtworkOrders = () => {
  const [orders, setOrders] = useState<CustomerArtworkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOrders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('artwork_orders')
        .select(`
          id,
          original_image_url,
          generated_image_url,
          selected_style,
          format_size,
          price_cents,
          status,
          customer_name,
          payment_status,
          finish_type,
          frame_option,
          support_material,
          special_instructions,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching orders:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger vos générations.",
          variant: "destructive",
        });
        return;
      }

      // Transform the data to match our interface
      const transformedOrders: CustomerArtworkOrder[] = (data || []).map(order => ({
        ...order,
        status: order.status as OrderStatus,
      }));
      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors du chargement.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOrder = async (orderId: string, updates: Record<string, unknown>) => {
    try {
      const { error } = await supabase
        .from('artwork_orders')
        .update(updates)
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, ...updates } : order
      ));

      return true;
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la commande.",
        variant: "destructive",
      });
      return false;
    }
  };

  const getOrdersByStatus = (status: string) => {
    return orders.filter(order => order.status === status);
  };

  const getIncompleteOrders = () => {
    return orders.filter(order => 
      order.status === 'pending' || 
      order.status === 'processing' ||
      order.payment_status === 'pending'
    );
  };

  const getCompletedOrders = () => {
    return orders.filter(order => 
      order.status === 'completed' || 
      order.status === 'delivered' ||
      order.payment_status === 'paid'
    );
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  return {
    orders,
    loading,
    fetchOrders,
    updateOrder,
    getOrdersByStatus,
    getIncompleteOrders,
    getCompletedOrders,
  };
};